import { useState, useRef, useEffect, useCallback } from 'react';

interface UseCarouselOptions {
  total: number;
  visibleCards?: number;
  gap?: number;
  autoplay?: boolean;
  autoplayInterval?: number;
}

const useCarousel = ({
  total,
  visibleCards = 3,
  gap = 24,
  autoplay = false,
  autoplayInterval = 4000,
}: UseCarouselOptions) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const maxIndex = Math.max(0, total - visibleCards);

  const updateCardWidth = useCallback(() => {
    if (containerRef.current) {
      const containerW = containerRef.current.offsetWidth;
      const w = (containerW - gap * (visibleCards - 1)) / visibleCards;
      setCardWidth(w);
    }
  }, [gap, visibleCards]);

  useEffect(() => {
    updateCardWidth();
    const ro = new ResizeObserver(updateCardWidth);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [updateCardWidth]);

  const goTo = useCallback((index: number) => {
    if (isAnimating) return;
    const clamped = Math.max(0, Math.min(index, maxIndex));
    setIsAnimating(true);
    setCurrentIndex(clamped);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, maxIndex]);

  const prev = useCallback(() => goTo(currentIndex - 1), [goTo, currentIndex]);
  const next = useCallback(() => goTo(currentIndex + 1), [goTo, currentIndex]);

  // Autoplay logic
  useEffect(() => {
    if (!autoplay || isPaused || maxIndex === 0) return;

    autoplayRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        const next = prev + 1;
        return next > maxIndex ? 0 : next;
      });
    }, autoplayInterval);

    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [autoplay, isPaused, maxIndex, autoplayInterval]);

  const pauseAutoplay = useCallback(() => setIsPaused(true), []);
  const resumeAutoplay = useCallback(() => setIsPaused(false), []);

  const translateX = currentIndex * (cardWidth + gap);

  const trackStyle: React.CSSProperties = {
    gap: `${gap}px`,
    transform: `translateX(-${translateX}px)`,
    transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  };

  const itemStyle: React.CSSProperties = {
    minWidth: cardWidth > 0
      ? `${cardWidth}px`
      : `calc((100% - ${gap * (visibleCards - 1)}px) / ${visibleCards})`,
  };

  return {
    currentIndex,
    maxIndex,
    cardWidth,
    containerRef,
    trackStyle,
    itemStyle,
    prev,
    next,
    goTo,
    canPrev: currentIndex > 0,
    canNext: currentIndex < maxIndex,
    dotsCount: maxIndex + 1,
    isPaused,
    pauseAutoplay,
    resumeAutoplay,
  };
};

export default useCarousel;
